import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {Alert, Button, DatePicker, Form, Select, Switch, Tooltip} from 'antd';
import {CheckOutlined, ClockCircleOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {
  StyledExportWizard,
  StyledExportWizardFooter,
  StyledExportWizardOverlay,
  StyledExportWizardPendingIcon,
  StyledExportWizardPillGroup,
  StyledExportWizardProgress,
  StyledExportWizardProgressBar,
  StyledExportWizardReady,
  StyledExportWizardReadyIcon,
  StyledExportWizardRecap,
  StyledExportWizardSourceOption,
  StyledExportWizardStepHeader,
  StyledExportWizardStepPanel,
  StyledExportWizardSwitchRow,
} from './StyledExportWizard';
import Steps from '../../../Steps';
import RadioGroupIcon from '../../../RadioGroupIcon';
import RangePicker from '../../../RangePicker';
import ColumnEditor from './ColumnEditor';
import CardExportSelector from '../../../Select/CardExportSelector';
import {FilePdfBadgeIcon, FileXlsBadgeIcon, FileZipBadgeIcon} from '../../../../icons';
import {amountHelpers, exportColumnsHelpers, fileHelpers, objectHelpers, systemHelpers} from '../../../../utils/helpers';
import {useEmployeeEmail} from '../../../../hooks/user/useEmployeeEmail';
import {useIsBudgetsAvailable, useIsIcpTierSmall} from '../../../../hooks';
import {transactionActions} from '../../../../state/actions/transaction.actions';
import {EXPORT_ACCOUNT_KEYS} from '../../../../pages/TransactionsPage/transactionPage.constants';
import {
  deriveExportDateRange,
  EXPORT_SOURCE_TABS,
  EXPORT_TYPE_FORMATS,
  EXPORT_TYPES,
  getExportPeriodDate,
  getExportPeriodLabel,
  getExportPeriodOptions,
  getExportSourceTabOptions,
  getExportStepCount,
  PERIOD_OPTIONS,
} from './exportWizard.helpers';

const FILE_TYPES = Object.freeze({EXCEL: 'xls', CSV: 'csv'});

const {Item} = Form;
const {DOCUMENTS, SPREADSHEET, STATEMENT} = EXPORT_TYPES;
const {CUSTOM} = PERIOD_OPTIONS;
const {CARDS, ACCOUNTS} = EXPORT_SOURCE_TABS;
const {WITHLESS} = EXPORT_ACCOUNT_KEYS;

// A stable reference for the `filter` default prop -- `{}` as a default parameter is a fresh
// object every render, and `filter` sits in the documents-summary effect's deps below. TableFilters
// always passes its own (stable, state-backed) `filter`, so this was latent; any other caller
// omitting the prop would retrigger that effect, and its fetch, on every render.
const EMPTY_FILTER = Object.freeze({});

const defaultFormValues = {
  type: SPREADSHEET,
  period: PERIOD_OPTIONS.THIS_MONTH,
  date: undefined,
  date_range: [],
  include_documents: false,
  include_authorized: false,
  source_tab: CARDS,
  card: WITHLESS,
};

const timeFormat = 'YYYY-MM-DD';

const ExportWizard = ({
  accountOptions = null,
  defaultAccount,
  defaultCardId = WITHLESS,
  error,
  fileUrl,
  filter = EMPTY_FILTER,
  handleCancel,
  handleExportSomethingElse: onExportSomethingElse,
  handleOk,
  loading = false,
  open = false,
  // Set instead of `fileUrl` for a format the backend only ever emails a link for (Statement's
  // forced-on receipts, Documents/zip) -- TableFilters.jsx skips polling for these and resolves
  // the export as soon as the job is enqueued, see `handleExportTransactions`'s `skipPolling`.
  pendingFileName,
  title,
  ...rest
}) => {
  const dispatch = useDispatch();
  const [t] = useTranslation('main');
  const employeeEmail = useEmployeeEmail();
  const [form] = Form.useForm();
  const [step, setStep] = useState(1);
  const [documentsSummary, setDocumentsSummary] = useState({loading: false, data: null, error: null});
  // The Form unmounts for the progress and done states, so `Form.useWatch` returns `undefined`
  // there -- snapshot what those states need to show while the Form is still mounted, inside
  // handleSubmit, and never read a live watch value once submission has started.
  const [submittedSnapshot, setSubmittedSnapshot] = useState(null);
  // The receipts-forcing effect below cannot gate on the watched `type` (see `isStatement`
  // below): a failed export or a cancelled SCA challenge remounts the Form the same way the
  // progress/done states do, and the watch reads `undefined` (falls back to the default) for
  // one render around that remount. `committedType` tracks the same field through the Form's
  // own `onValuesChange` instead (ExportModal.jsx:163-165's pattern) -- that only fires from a
  // field's own onChange, never from the remount's `registerField`, so it cannot flicker -- plus
  // the three places the code resets the form programmatically (the `open` effect,
  // `handleExportSomethingElse` and `handleAfterClose`).
  const [committedType, setCommittedType] = useState(defaultFormValues.type);
  // Same "committed" rationale as committedType above -- the receipts-force effect below now also
  // reacts to the Cards-tab pick, so that pick needs the same protection against the Form-remount
  // blip (a cancelled SCA challenge/failed export must not spuriously un-force the toggle).
  const [committedSourceTab, setCommittedSourceTab] = useState(defaultFormValues.source_tab);
  const [committedCardId, setCommittedCardId] = useState(defaultFormValues.card);
  // The Cards-tab picker's own fetched data lives inside CardExportSelector, not here -- this is
  // just the display label it reports back for whatever card is currently selected (plan §5
  // "Data from" tabs), used for the done-state recap line. Kept as a single `{id, label}` pair,
  // not two separate pieces of state, so a label resolving for a card the user has since moved
  // away from can never be mistaken for the current one's label -- every reader below checks
  // `resolvedCard.id === cardId` before trusting it (bug report follow-up: a stale label from the
  // *previous* pick -- or, worse, from the "All cards" sentinel itself resolving on mount -- was
  // getting attached to whichever card id came next, and `CardExportSelector`'s own dedup then hid
  // the real option underneath it, permanently locking the Select onto the wrong display text).
  const [resolvedCard, setResolvedCard] = useState(null);
  const forcedReceiptsKeyRef = useRef(null);
  // Same key-tracking pattern as `forcedReceiptsKeyRef`, for `include_authorized` ("Include pending
  // transactions") -- see the effect below.
  const forcedAuthorizedKeyRef = useRef(null);
  // Step 3's file-type radio (7b) -- plain state, never a Form field: it drives `format` directly
  // and nothing derives a forced value from it, so it has no need of the committedType class of
  // fix above. Defaults to Excel per the ticket.
  const [fileType, setFileType] = useState(FILE_TYPES.EXCEL);
  // The saved column default (7b) -- also plain state, loaded from localStorage in the `open`
  // effect and written on every add/remove. Holds the user's full selection, never filtered for
  // eligibility (that happens below, at render and send time) -- see exportColumns.helpers.js.
  const [selectedColumnKeys, setSelectedColumnKeys] = useState(exportColumnsHelpers.defaultKeys);
  const [droppedColumnCount, setDroppedColumnCount] = useState(0);
  // Pinned once, in the `open` effect below, not read fresh on every render (plan §5 story 7b
  // security review): the stored company can move while the wizard is open (another tab, a
  // 403/boot fallback -- `companyHeader.js`), and a late read would write one company's column
  // selection into another's storage slot and label its download with the wrong company's data.
  const [companyId, setCompanyId] = useState(null);

  // `{form, preserve: true}` (not the bare `form` shorthand) is load-bearing: by default
  // `Form.useWatch` reads only the currently-*mounted* fields, so a field whose `Item` lives in a
  // step panel other than the active one (every field here except nothing -- each one's Item is
  // only rendered on its own step) reads back as `undefined`, silently collapsing to the `??`
  // default -- period/card/account/date all reverting to "Last month"/"All cards" the moment step
  // 2 is left, in the footer recap AND in the actual submitted query (design review: "always shows
  // 'All cards Last month', never what the user picked"). `preserve: true` makes the watch read
  // `form`'s full store instead, which keeps every field's value regardless of which step's Item is
  // currently mounted (rc-field-form's Form-level `preserve`, default true, already keeps the
  // value in that store on unmount -- this is `Form.useWatch`'s own separate opt-in to read it).
  const type = Form.useWatch('type', {form, preserve: true}) ?? defaultFormValues.type;
  const period = Form.useWatch('period', {form, preserve: true}) ?? defaultFormValues.period;
  const date = Form.useWatch('date', {form, preserve: true});
  const dateRange = Form.useWatch('date_range', {form, preserve: true});
  const account = Form.useWatch('account', {form, preserve: true});
  const sourceTab = Form.useWatch('source_tab', {form, preserve: true}) ?? defaultFormValues.source_tab;
  const cardId = Form.useWatch('card', {form, preserve: true}) ?? defaultFormValues.card;

  const isStatement = type === STATEMENT;
  const isCommittedStatement = committedType === STATEMENT;
  const isDocuments = type === DOCUMENTS;
  const isSpreadsheet = type === SPREADSHEET;
  // Only Spreadsheet's step 3 offers a file-type choice; every other tile keeps its one format.
  const format = isSpreadsheet ? fileType : EXPORT_TYPE_FORMATS[type];
  const stepCount = getExportStepCount(type);

  // Eligibility (ICP tier, the budgets flag) is read here, not in exportColumns.helpers.js,
  // because it needs these two hooks (plan §5 story 7b precision fixes) -- filtered for display
  // and for the request, never written back to storage, so a company that loses eligibility for
  // a column keeps it saved until eligibility returns.
  const isTierSmall = useIsIcpTierSmall();
  const hasBudgets = useIsBudgetsAvailable();
  const isColumnEligible = (column) => (
    !(isTierSmall && column.excluded_tiers.includes('small'))
    && !(column.requires_flag === 'budgets' && !hasBudgets)
  );
  const eligibleColumns = useMemo(
    () => exportColumnsHelpers.selectableColumns.filter(isColumnEligible),
    [isTierSmall, hasBudgets], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const eligibleKeys = useMemo(() => new Set(eligibleColumns.map((column) => column.key)), [eligibleColumns]);
  const eligibleSelectedKeys = useMemo(
    () => selectedColumnKeys.filter((key) => eligibleKeys.has(key)),
    [selectedColumnKeys, eligibleKeys],
  );
  const eligibleDefaultKeys = useMemo(
    () => exportColumnsHelpers.defaultKeys.filter((key) => eligibleKeys.has(key)),
    [eligibleKeys],
  );
  const isColumnsDefaultSet = exportColumnsHelpers.sameKeySet(eligibleSelectedKeys, eligibleDefaultKeys);
  const getColumnExample = (key) => t(`exportWizard.columnExample.${key}`, {defaultValue: '-'});

  const tileOptions = useMemo(() => [
    {
      value: STATEMENT,
      label: t('exportWizard.tile.statement'),
      description: t('exportWizard.tileDescription.statement'),
      icon: <FilePdfBadgeIcon />,
      className: 'badge-icon',
    },
    {
      value: SPREADSHEET,
      label: t('exportWizard.tile.spreadsheet'),
      description: t('exportWizard.tileDescription.spreadsheet'),
      icon: <FileXlsBadgeIcon />,
      className: 'badge-icon',
    },
    {
      value: DOCUMENTS,
      label: t('exportWizard.tile.documents'),
      description: t('exportWizard.tileDescription.documents'),
      icon: <FileZipBadgeIcon />,
      className: 'badge-icon',
    },
  ], [t]);

  const periodOptions = useMemo(
    () => getExportPeriodOptions(type).map((value) => ({value, label: t(`exportWizard.period.${value}`)})),
    [type, t],
  );

  // "Does this company have any Open Banking accounts at all", independent of the current tile's
  // format -- used below for the company-level defaults/fallbacks, as opposed to `hasSourceSelector`,
  // which also gates on the current tile actually having something to offer on that tab.
  const accountTabOptions = useMemo(
    () => getExportSourceTabOptions(accountOptions, type, WITHLESS),
    [accountOptions, type],
  );
  const hasAnyAccountOptions = Array.isArray(accountOptions) && accountOptions.length > 0;
  // Statement (pdf) is Withless-only -- OB_ACCOUNT_EXPORT_FORMATS never includes 'pdf', so
  // `accountTabOptions` is always `[]` for it. Gating on that (not just on the company having *some*
  // accounts) hides the Cards/Accounts selector entirely for Statement instead of showing a
  // permanently-disabled "Accounts" segment (bug report scenario 4, "remove Account (ONLY for PDF)").
  const hasSourceSelector = hasAnyAccountOptions && accountTabOptions.length > 0;
  // Seeding the wizard's own already-resolved card label as an extra option (alongside "All cards")
  // keeps the picker's `value` matching an option across a remount -- CardExportSelector unmounts
  // whenever step 2 isn't the active step panel, and without this its single-card hydration fetch
  // has to complete before the Select can show anything but the raw card id (bug report scenario 1,
  // the "shows card_id" glitch on Back).
  const allCardsOptions = useMemo(() => {
    const options = [{value: WITHLESS, label: t('exportWizard.allCards')}];
    if (cardId !== WITHLESS && resolvedCard?.id === cardId) options.push({value: cardId, label: resolvedCard.label});
    return options;
  }, [t, cardId, resolvedCard]);
  // The sentinels (ALL_OPEN_BANKING/WITHLESS) carry no `balance`, so they render as plain text --
  // only a real OB account gets the right-aligned amount next to its name.
  const renderSourceOption = (option) => (
    <StyledExportWizardSourceOption>
      <span className='source-option-label'>{option.data?.label}</span>
      {option.data?.balance != null && (
        <span className='source-option-balance'>
          {amountHelpers.getAmountWithCurrencyCode(option.data.balance, {currencyCode: option.data.currencyCode})}
        </span>
      )}
    </StyledExportWizardSourceOption>
  );
  const isSingleCardSelected = sourceTab === CARDS && cardId !== WITHLESS;

  // Only Statement's Custom uses a month (today's pdf picker, ending at last month); every other
  // tile's Custom is a date range (plan §5 story 7, "Period").
  const resolvedDate = isStatement && period === CUSTOM ? date : getExportPeriodDate(period);

  // A role with no Withless account filters a tile's Accounts-tab list to `[]`, not `null` --
  // `hasAnyAccountOptions` stays true. Block submit rather than fall through with no account picked.
  // The Cards tab never goes empty (every tile's format is Cards-eligible -- see
  // EXPORT_SOURCE_TABS's comment), so this guard only ever applies on the Accounts tab. Spreadsheet's
  // own last condition only applies on its own final step -- step 2's Next must not be blocked by a
  // column count the user has not reached yet.
  const disabledSubmit = loading || (hasAnyAccountOptions && sourceTab === ACCOUNTS && !accountTabOptions.length) || (period === CUSTOM && (
    isStatement ? !objectHelpers.isValidValue(date) : !(dateRange && dateRange.length === 2)
  )) || (isSpreadsheet && step === stepCount && eligibleSelectedKeys.length === 0);

  useEffect(() => {
    if (!open) {
      forcedReceiptsKeyRef.current = null;
      forcedAuthorizedKeyRef.current = null;
      return;
    }
    // Pin the company for the whole time the wizard is open -- see the `companyId` state comment
    // above. Read once, here, not from the `companyId` state (which hasn't updated yet this tick).
    const {companyId: openedCompanyId} = systemHelpers.getAuthDetails();
    setCompanyId(openedCompanyId);
    // Default into the Accounts tab only when the page's own active source already points at a
    // specific Open Banking account (the per-account transactions sub-page) -- every other case
    // starts on Cards, same place "Withless" used to be the sole implicit source.
    const initialSourceTab = hasAnyAccountOptions && defaultAccount && defaultAccount !== WITHLESS ? ACCOUNTS : CARDS;
    form.setFieldsValue({...defaultFormValues, account: defaultAccount, card: defaultCardId, source_tab: initialSourceTab});
    setCommittedType(defaultFormValues.type);
    setCommittedSourceTab(initialSourceTab);
    setCommittedCardId(defaultCardId);
    setStep(1);
    setSubmittedSnapshot(null);
    setFileType(FILE_TYPES.EXCEL);
    // The saved column default loads here, and only here -- not on "Export something else" or on
    // close, which would throw the saved set away the moment either fires (plan §5 story 7b
    // precision fixes, "The column set is reset on one path, not three").
    const {keys, droppedKeys} = exportColumnsHelpers.getSavedColumns(openedCompanyId);
    setSelectedColumnKeys(keys);
    setDroppedColumnCount(droppedKeys.length);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // `include_documents` defaults to on for every Statement export (bug report follow-up: it used to
  // be force-disabled on, which meant it could never be turned off -- it is now a real opt-out, keyed
  // on the committed source/card so picking a new card resets it to the default instead of carrying
  // over a stale manual choice. The key-tracking guard protects that manual "off" choice from a
  // Form-remount (a failed export, a cancelled SCA challenge) spuriously resetting it -- gated on the
  // committed values, not the watched ones (see the comment on `committedType`/`committedSourceTab`
  // above).
  useEffect(() => {
    if (!isCommittedStatement) {
      forcedReceiptsKeyRef.current = null;
      return;
    }
    const key = String(committedSourceTab === CARDS && committedCardId !== WITHLESS);
    if (forcedReceiptsKeyRef.current === key) return;
    forcedReceiptsKeyRef.current = key;
    form.setFieldValue('include_documents', true);
  }, [isCommittedStatement, committedSourceTab, committedCardId]); // eslint-disable-line react-hooks/exhaustive-deps

  // `include_authorized` ("Include pending transactions") defaults on for a single selected card on
  // the Cards tab, for every export type -- bug report: it used to be a free opt-in Switch on
  // Spreadsheet only, and on Statement a value set directly on the Form with no switch row at all
  // (the old ExportModal's sibling rule: a single-card export already switches to the
  // authorization-date basis), so Statement and Documents had no way to opt in/out of it at all. Now
  // a single defaulting effect covers every tile, keyed the same way `include_documents` above is --
  // a manual choice must survive a Form-remount without being spuriously reset.
  useEffect(() => {
    if (committedSourceTab !== CARDS) {
      forcedAuthorizedKeyRef.current = null;
      return;
    }
    const committedSingleCard = committedCardId !== WITHLESS;
    const key = String(committedSingleCard);
    if (forcedAuthorizedKeyRef.current === key) return;
    forcedAuthorizedKeyRef.current = key;
    form.setFieldValue('include_authorized', committedSingleCard);
  }, [committedSourceTab, committedCardId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each tile filters the Accounts-tab list to its own format, so an account valid for one tile can
  // be invalid for another -- re-pick rather than carry over a value the new list does not offer.
  // Scoped to the Accounts tab: the Cards tab's own value (`card`) never needs this, since "All
  // cards" is valid for every tile.
  useEffect(() => {
    if (!open || !hasSourceSelector || sourceTab !== ACCOUNTS) return;
    const isValid = accountTabOptions.some((option) => option.value === form.getFieldValue('account'));
    if (!isValid) form.setFieldValue('account', accountTabOptions[0]?.value);
  }, [open, hasSourceSelector, sourceTab, accountTabOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Statement and Documents are Withless-only formats (EXPORT_TYPE_FORMATS), so switching to one of
  // them while on the Accounts tab empties that tab's list -- fall back to Cards rather than strand
  // the user on a tab (now hidden, see `hasSourceSelector` above) with nothing to pick.
  useEffect(() => {
    if (!open || sourceTab !== ACCOUNTS) return;
    if (hasAnyAccountOptions && accountTabOptions.length === 0) form.setFieldValue('source_tab', CARDS);
  }, [open, sourceTab, hasAnyAccountOptions, accountTabOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Statement's own option list has no "This month" (see getExportPeriodOptions) -- switching to it
  // while "This month" is the picked period (the default, now that it leads the list) would otherwise
  // leave the pill group matching no option at all. Same re-pick pattern as the account fallback above.
  useEffect(() => {
    if (!open) return;
    const isValid = periodOptions.some((option) => option.value === form.getFieldValue('period'));
    if (!isValid) form.setFieldValue('period', periodOptions[0]?.value);
  }, [open, periodOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // The Documents recap (plan §5 story 7, "The Documents recap"). Send the export's own query
  // shape -- date_gte, date_lte, filter -- the same shape `handleExportTransactions` spreads for
  // every non-pdf format (TableFilters.jsx:365), not `account`, which `/transactions/download`
  // does not accept, so the recap resolves the same scope the archive will. Fetch is informational
  // only: a failed fetch hides the line rather than blocking the step.
  useEffect(() => {
    if (!isDocuments || !open) return;
    // Custom with no range picked yet has nothing to summarize -- without this guard,
    // deriveExportDateRange falls back to last month (its "invalid range" default), silently
    // previewing the wrong period until the user finishes picking one.
    if (period === CUSTOM && !(dateRange && dateRange.length === 2)) {
      setDocumentsSummary({loading: false, data: null, error: null});
      return;
    }
    // `ignore` guards against a slow response for a period the user has since moved away from
    // overwriting a newer one that resolved first -- this effect has no other way to cancel the
    // in-flight request.
    let ignore = false;
    // Only trust `dateRange` while Custom is the active period -- switching back to a preset
    // (Last month/This month) doesn't clear the RangePicker's own Form field, so a stale Custom
    // selection would otherwise outlive the period it was picked for and keep previewing (and,
    // without this guard, exporting) that old range instead of the newly picked preset month
    // (bug report: period changed Custom -> This month, documents-summary still queried with the
    // old Custom date_gte/date_lte). Mirrors `handleSubmit`'s own `period === CUSTOM ? dateRange : []`.
    const [startDate, endDate] = deriveExportDateRange(format, resolvedDate, period === CUSTOM ? dateRange : []);
    // The wizard's own Cards-tab pick scopes the preview the same way it will scope the real
    // download (see handleSubmit/TableFilters.jsx's `cardId` handling) -- without this, picking one
    // card here would still preview the whole wallet's file/missing-document counts.
    const cardFilter = isSingleCardSelected ? {card_id_in: [cardId]} : {};
    setDocumentsSummary({loading: true, data: null, error: null});
    dispatch(transactionActions.getTransactionsDocumentsSummary({
      query: {date_gte: startDate.format(timeFormat), date_lte: endDate.format(timeFormat), ...filter, ...cardFilter},
      successCallback: (data) => { if (!ignore) setDocumentsSummary({loading: false, data, error: null}); },
      errorCallback: () => { if (!ignore) setDocumentsSummary({loading: false, data: null, error: true}); },
    }));
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDocuments, open, period, date?.valueOf(), dateRange, filter, isSingleCardSelected, cardId]);

  const handleBack = () => setStep((prevStep) => Math.max(1, prevStep - 1));

  const handleNext = () => setStep((prevStep) => Math.min(stepCount, prevStep + 1));

  const handleExportSomethingElse = () => {
    const initialSourceTab = hasAnyAccountOptions && defaultAccount && defaultAccount !== WITHLESS ? ACCOUNTS : CARDS;
    form.setFieldsValue({...defaultFormValues, account: defaultAccount, card: defaultCardId, source_tab: initialSourceTab});
    setCommittedType(defaultFormValues.type);
    setCommittedSourceTab(initialSourceTab);
    setCommittedCardId(defaultCardId);
    setStep(1);
    setFileType(FILE_TYPES.EXCEL);
    onExportSomethingElse?.();
  };

  const handleValuesChange = (changedValues) => {
    if ('type' in changedValues) setCommittedType(changedValues.type);
    if ('source_tab' in changedValues) setCommittedSourceTab(changedValues.source_tab);
    if ('card' in changedValues) setCommittedCardId(changedValues.card);
  };

  const handleAddColumn = (key) => {
    const keys = [...selectedColumnKeys, key];
    setSelectedColumnKeys(keys);
    setDroppedColumnCount(0);
    exportColumnsHelpers.saveColumns(companyId, keys);
  };

  const handleRemoveColumn = (key) => {
    const keys = selectedColumnKeys.filter((selectedKey) => selectedKey !== key);
    setSelectedColumnKeys(keys);
    setDroppedColumnCount(0);
    exportColumnsHelpers.saveColumns(companyId, keys);
  };

  const handleResetColumns = () => {
    setSelectedColumnKeys(exportColumnsHelpers.defaultKeys);
    setDroppedColumnCount(0);
    exportColumnsHelpers.resetColumns();
  };

  // The source and period labels, and which tile was submitted, must be snapshotted here -- the
  // Form unmounts for the progress/done states right after this call, and `useWatch` returns
  // `undefined` once it does.
  const sourceLabel = sourceTab === ACCOUNTS
    ? accountTabOptions.find((option) => option.value === account)?.label
    : (cardId === WITHLESS ? t('exportWizard.allCards') : (resolvedCard?.id === cardId ? resolvedCard.label : undefined));
  const periodLabel = getExportPeriodLabel({date, dateRange, isStatement, period, periodOptions});

  const handleSubmit = () => {
    if (disabledSubmit) return;
    // `type` is `useWatch`-derived -- snapshot it too, or the progress/done states read the Form's
    // post-unmount fallback (`spreadsheet`) instead of the tile that was actually submitted (design
    // review J2). `fileCount` likewise -- the done screen's one-line recap needs it and
    // `documentsSummary` is itself tied to this same Form lifetime.
    setSubmittedSnapshot({
      type,
      sourceLabel,
      periodLabel,
      fileCount: isDocuments ? documentsSummary.data?.documents : undefined,
    });
    handleOk({
      format,
      date: resolvedDate,
      date_range: period === CUSTOM && !isStatement ? dateRange : [],
      sourceTab,
      account,
      // The sentinel ("All cards") is not a real card id -- only forward it when it names an
      // actual one, so TableFilters.jsx never builds a `card_id_in: ['withless']` filter.
      cardId: sourceTab === CARDS && cardId !== WITHLESS ? cardId : undefined,
      include_documents: isStatement ? Boolean(form.getFieldValue('include_documents')) : undefined,
      // Only rendered (and only meaningful) on the Cards tab -- see the switch row above;
      // `TableFilters.jsx`'s `handleExportTransactions` already forwards this field in the query
      // whenever it's truthy. Documents (zip) and Statement (pdf) have no toggle for this -- always
      // forced on/off respectively, regardless of the (unmounted, stale) form field value.
      include_authorized: sourceTab === CARDS
        ? (isDocuments ? true : (isStatement ? false : Boolean(form.getFieldValue('include_authorized'))))
        : undefined,
      // Omitted (not an empty array) when the selection is the standard default set: a non-`None`
      // `columns` makes the backend's monthly PDF cache ineligible, and omitting also keeps a
      // default Spreadsheet export byte-identical to today's file (plan §5 story 7b, story 4's
      // rule). Never the raw `selectedColumnKeys` -- only the eligible subset is ever sent.
      columns: isSpreadsheet && !isColumnsDefaultSet ? eligibleSelectedKeys : undefined,
      // The pinned snapshot, not a fresh storage read -- so the caller can send it as an explicit
      // header instead of letting the request layer re-read whatever company is selected now.
      companyId,
    });
  };

  const handleAfterClose = () => {
    form.setFieldsValue(defaultFormValues);
    setCommittedType(defaultFormValues.type);
    setCommittedSourceTab(defaultFormValues.source_tab);
    setCommittedCardId(defaultFormValues.card);
    setFileType(FILE_TYPES.EXCEL);
    setResolvedCard(null);
  };

  const fileName = fileUrl ? fileHelpers.getFileNameFromUrl(fileUrl) : null;

  const getFooter = () => {
    if (fileUrl || pendingFileName) {
      return [
        <Button key='exportMore' onClick={handleExportSomethingElse} size='large'>{t('exportWizard.exportSomethingElse')}</Button>,
        <Button key='done' onClick={handleCancel} size='large' type='primary'>{t('done')}</Button>,
      ];
    }
    if (loading) return [];

    let buttons;
    if (step === 1) {
      // Ticket's Navigation wording: "Cancel on step 1, the close button and a click outside
      // close the wizard" -- Cancel is a step-1-only button, distinct from the ×.
      buttons = [
        <Button key='cancel' onClick={handleCancel} size='large'>{t('cancel')}</Button>,
        <Button key='next' onClick={handleNext} size='large' type='primary'>{t('next')}</Button>,
      ];
    } else {
      const backButton = <Button key='back' onClick={handleBack} size='large'>{t('back')}</Button>;
      if (isStatement) {
        buttons = [backButton, <Button key='submit' disabled={disabledSubmit} loading={loading} onClick={handleSubmit} size='large' type='primary'>{t('exportWizard.downloadPdf')}</Button>];
      } else if (isDocuments) {
        buttons = [backButton, <Button key='submit' disabled={disabledSubmit} loading={loading} onClick={handleSubmit} size='large' type='primary'>{t('exportWizard.downloadZip')}</Button>];
      } else if (isSpreadsheet && step === stepCount) {
        buttons = [backButton, <Button key='submit' disabled={disabledSubmit} loading={loading} onClick={handleSubmit} size='large' type='primary'>{t('download')}</Button>];
      } else {
        buttons = [backButton, <Button key='next' disabled={disabledSubmit} onClick={handleNext} size='large' type='primary'>{t('next')}</Button>];
      }
    }

    // The source/period recap (mock: "Marco Rossi · September 2026") only once the user has
    // reached the tile's own final, submit-able step -- step 1 has nothing to recap yet, and
    // Spreadsheet's in-between step 2 hasn't collected a period/source worth repeating.
    const recap = step === stepCount && sourceLabel && periodLabel ? `${sourceLabel} · ${periodLabel}` : null;

    return (
      <StyledExportWizardFooter>
        <span className='recap'>{recap}</span>
        <span className='buttons'>{buttons}</span>
      </StyledExportWizardFooter>
    );
  };

  return (
    <StyledExportWizard
      afterClose={handleAfterClose}
      footer={getFooter()}
      onCancel={handleCancel}
      open={open}
      title={pendingFileName ? t('exportWizard.pending.title') : (title || t('export'))}
      width={488}
      {...rest}
    >
      {fileUrl ? (
        <StyledExportWizardOverlay>
          <StyledExportWizardReady>
            <StyledExportWizardReadyIcon>
              <CheckOutlined />
            </StyledExportWizardReadyIcon>
            <h3>{fileName}</h3>
            <span>
              {submittedSnapshot?.fileCount != null
                ? t('exportWizard.ready.recapWithCount', {
                  source: submittedSnapshot.sourceLabel,
                  period: submittedSnapshot.periodLabel,
                  count: submittedSnapshot.fileCount,
                })
                : t('exportWizard.ready.recap', {source: submittedSnapshot?.sourceLabel, period: submittedSnapshot?.periodLabel})}
            </span>
          </StyledExportWizardReady>
        </StyledExportWizardOverlay>
      ) : pendingFileName ? (
        <StyledExportWizardOverlay>
          <StyledExportWizardReady>
            <StyledExportWizardPendingIcon>
              <ClockCircleOutlined />
            </StyledExportWizardPendingIcon>
            <h3>{t('exportWizard.pending.heading')}</h3>
            <span>{t('exportWizard.pending.body', {filename: pendingFileName, email: employeeEmail})}</span>
          </StyledExportWizardReady>
        </StyledExportWizardOverlay>
      ) : loading ? (
        <StyledExportWizardProgress>
          {/* An indeterminate sliding fill, not the step-dot bar -- `downloadWithBackgroundJob` polls
              with no real percentage to report, and the dot bar (always fully filled here) read as
              static rather than "in progress" (bug report scenario 2). This state can hold a while,
              and previously vanished along with the Form -- `submittedSnapshot.type`, not the live
              `type`, since `useWatch` reads the Form's post-unmount fallback once it does. */}
          {submittedSnapshot && <h3>{t('exportWizard.preparing', {type: t(`exportWizard.tile.${submittedSnapshot.type}`)})}</h3>}
          <StyledExportWizardProgressBar />
        </StyledExportWizardProgress>
      ) : (
        <>
          <StyledExportWizardStepHeader>
            <span className='step-count'>{t('step')} {step} {t('of')} {stepCount}</span>
            <h3>{t(`exportWizard.step.title.${step}`)}</h3>
          </StyledExportWizardStepHeader>
          <Steps activeNum={step} quantity={stepCount} />
          <Form form={form} initialValues={defaultFormValues} layout='vertical' onValuesChange={handleValuesChange}>
            <StyledExportWizardStepPanel>
              {step === 1 && (
                <Item name='type'>
                  <RadioGroupIcon options={tileOptions} />
                </Item>
              )}
              {step === 2 && (
                <div>
                {hasSourceSelector && (
                  <Item label={t('exportWizard.dataFrom')} name='source_tab'>
                    <StyledExportWizardPillGroup
                      optionType='button'
                      options={[
                        {value: CARDS, label: t('exportWizard.tabs.cards')},
                        {value: ACCOUNTS, label: t('exportWizard.tabs.accounts')},
                      ]}
                    />
                  </Item>
                )}
                {sourceTab === ACCOUNTS ? (
                  <Item label={t('source')} name='account'>
                    <Select optionRender={renderSourceOption} options={accountTabOptions} size='middle' />
                  </Item>
                ) : (
                  <Item label={t('source')} name='card'>
                    <CardExportSelector
                      extraOptions={allCardsOptions}
                      // Always records the pair, never compares against the live `cardId` here --
                      // a stale closure of this callback (fired for a card the user has since moved
                      // away from) would otherwise still pass an `=== cardId` check that happens to
                      // be true again by the time it runs, re-poisoning `resolvedCard` with the
                      // *previous* pick's label. `allCardsOptions`/`sourceLabel` above are the ones
                      // that check `resolvedCard.id === cardId` before trusting it, which is race-free
                      // (bug report follow-up: the Select was getting stuck showing a stale label --
                      // "All cards" on mount, then the previously-picked card -- for whichever card
                      // was actually selected, because `CardExportSelector`'s own dedup filter hid the
                      // real option underneath the mislabeled `extraOptions` entry).
                      onResolveLabel={(resolvedValue, label) => {
                        if (resolvedValue === WITHLESS) return;
                        // The functional updater's early-return-same-reference is load-bearing, not
                        // a style choice: `allCardsOptions`'s `extraOptions` depend on `resolvedCard`,
                        // which `CardExportSelector`'s own `options` depend on, which is this
                        // callback's own effect dependency -- an unconditional `setResolvedCard`
                        // (a fresh object every call) would re-fire this same callback every render,
                        // forever.
                        setResolvedCard((prev) => (
                          prev?.id === resolvedValue && prev?.label === label ? prev : {id: resolvedValue, label}
                        ));
                      }}
                      size='middle'
                    />
                  </Item>
                )}
                <Item label={t('exportWizard.period.label')} name='period'>
                  <StyledExportWizardPillGroup optionType='button' options={periodOptions} />
                </Item>
                {period === CUSTOM && (isStatement ? (
                  <Item name='date'>
                    <DatePicker
                      // Computed per call, not once at module load -- a tab left open across a
                      // month boundary must not keep yesterday's cutoff.
                      disabledDate={(current) => current.isAfter(dayjs().subtract(1, 'month').endOf('month'))}
                      format='MMM YYYY'
                      picker='month'
                      placeholder={t('selectDate')}
                      size='middle'
                    />
                  </Item>
                ) : (
                  <Item name='date_range'>
                    <RangePicker disabledDate={(current) => current.isAfter(dayjs())} size='middle' />
                  </Item>
                ))}
                {sourceTab === CARDS && isSpreadsheet && (
                  // Withless-only (ExportModal's old `accounts: [WITHLESS]` restriction) -- pending
                  // transactions are not a concept Open Banking accounts have, so this never shows
                  // on the Accounts tab. Only Spreadsheet renders this toggle -- Documents (zip)
                  // always includes pending transactions and Statement (pdf) always excludes them,
                  // neither with an opt-out, so both are forced in handleSubmit's
                  // `include_authorized` with no toggle shown.
                  <StyledExportWizardSwitchRow>
                    <div className='switch-row'>
                      <span className='switch-row-label'>
                        {t('includePendingTransactions')}
                        <Tooltip overlayInnerStyle={{fontSize: 12}} title={t('pendingTransactionsTooltip')}>
                          <InfoCircleOutlined className='switch-row-info' />
                        </Tooltip>
                      </span>
                      <Item name='include_authorized' noStyle valuePropName='checked'>
                        <Switch />
                      </Item>
                    </div>
                  </StyledExportWizardSwitchRow>
                )}
                {isStatement && (
                  // Defaults to on for every Statement export (see the defaulting effect above), but
                  // a real opt-in the user can turn off -- bug report: it was previously forced
                  // `disabled`, so there was no way to export a PDF without receipts/invoices.
                  <StyledExportWizardSwitchRow>
                    <div className='switch-row'>
                      <span className='switch-row-label'>
                        {t('exportWizard.receiptsToggleLabel')}
                        <Tooltip overlayInnerStyle={{fontSize: 12}} title={t('exportWizard.receiptsToggleDescription')}>
                          <InfoCircleOutlined className='switch-row-info' />
                        </Tooltip>
                      </span>
                      <Item name='include_documents' noStyle valuePropName='checked'>
                        <Switch />
                      </Item>
                    </div>
                  </StyledExportWizardSwitchRow>
                )}
                {isDocuments && !documentsSummary.error && documentsSummary.data && (
                  <StyledExportWizardRecap>
                    {t('exportWizard.documentsRecap', {
                      documents: documentsSummary.data.documents,
                      missing: documentsSummary.data.transactions_without_document,
                    })}
                  </StyledExportWizardRecap>
                )}
                </div>
              )}
              {step === 3 && (
                <div>
                <Item label={t('exportWizard.fileType.label')}>
                  {/* Plain pills, no icons -- examples/04/05's "File type" row is Excel/CSV text
                      only, unlike step 1's icon tiles. */}
                  <StyledExportWizardPillGroup
                    onChange={(event) => setFileType(event.target.value)}
                    optionType='button'
                    options={[
                      {value: FILE_TYPES.EXCEL, label: t('exportWizard.fileType.excel')},
                      {value: FILE_TYPES.CSV, label: t('exportWizard.fileType.csv')},
                    ]}
                    value={fileType}
                  />
                </Item>
                <ColumnEditor
                  eligibleColumns={eligibleColumns}
                  getExampleValue={getColumnExample}
                  isDefaultSet={isColumnsDefaultSet}
                  lockedColumns={exportColumnsHelpers.lockedColumns}
                  onAdd={handleAddColumn}
                  onRemove={handleRemoveColumn}
                  onReset={handleResetColumns}
                  selectedKeys={eligibleSelectedKeys}
                />
                {droppedColumnCount > 0 && (
                  <StyledExportWizardRecap>
                    {t('exportWizard.columnEditor.droppedNotice', {count: droppedColumnCount})}
                  </StyledExportWizardRecap>
                )}
                </div>
              )}
            </StyledExportWizardStepPanel>
          </Form>
          {error && <Alert message={error} showIcon type='error' />}
        </>
      )}
    </StyledExportWizard>
  );
};

ExportWizard.propTypes = {
  accountOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    formats: PropTypes.arrayOf(PropTypes.string).isRequired,
    balance: PropTypes.number,
    currencyCode: PropTypes.string,
  })),
  defaultAccount: PropTypes.string,
  defaultCardId: PropTypes.string,
  error: PropTypes.string,
  fileUrl: PropTypes.string,
  filter: PropTypes.object,
  handleCancel: PropTypes.func,
  handleExportSomethingElse: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  open: PropTypes.bool,
  pendingFileName: PropTypes.string,
  title: PropTypes.string,
};

export default ExportWizard;
